import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { POST_SORT, POST_SORT_DEFINITIONS, type TPostSort } from '@onesource/schemas';
import type { FC } from 'react';
import { MdOutlineSwapVert } from 'react-icons/md';

type Props = {
  value?: TPostSort;
  /** Emits undefined when the default (recent_activity) is picked, keeping the URL clean */
  onChange: (value: TPostSort | undefined) => void;
};

// Compact display labels for the closed control — the menu keeps the full
// labels from the schema. "Most recent activity" alone is ~200px wide.
const SHORT_LABELS: Record<TPostSort, string> = {
  [POST_SORT.RECENT_ACTIVITY]: 'Recent activity',
  [POST_SORT.NEWEST]: 'Newest',
  [POST_SORT.MOST_DISCUSSED]: 'Most discussed',
};

/**
 * Compact feed sort dropdown — most recent activity (default) and newest
 * first. The closed control shows a sort glyph + a short label so it stays
 * narrow next to the search input.
 */
export const FeedSortSelect: FC<Props> = ({ value, onChange }) => {
  const current = value ?? POST_SORT.RECENT_ACTIVITY;

  return (
    <Select
      size="small"
      value={current}
      onChange={e => {
        const next = e.target.value as TPostSort;
        onChange(next === POST_SORT.RECENT_ACTIVITY ? undefined : next);
      }}
      renderValue={selected => (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <MdOutlineSwapVert size={16} color="#666" style={{ flexShrink: 0 }} />
          <Box component="span" sx={{ fontWeight: 600, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {SHORT_LABELS[selected]}
          </Box>
        </Box>
      )}
      MenuProps={{
        slotProps: {
          paper: { sx: { borderRadius: '12px', mt: 0.5, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } },
        },
      }}
      sx={{
        // Match the borderless soft-grey filter trigger buttons alongside it.
        bgcolor: '#f4f4f4',
        fontFamily: 'Figtree, sans-serif',
        fontSize: 13,
        flexShrink: 0,
        transition: 'background-color 0.15s ease',
        // Sits in the 34px filter row on phones, next to the 40px search from md up.
        height: { xs: 34, md: 40 },
        // Theme forces OutlinedInput to a 20px pill; match the selector so the
        // radius lines up with the filter trigger buttons, and drop the outline.
        '&.MuiOutlinedInput-root': { borderRadius: '10px' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '&:hover': { bgcolor: '#ececec' },
      }}
    >
      {POST_SORT_DEFINITIONS.map(({ value: sortValue, label }) => (
        <MenuItem
          key={sortValue}
          value={sortValue}
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontSize: 13,
            borderRadius: '8px',
            mx: 0.75,
            my: 0.25,
            '&.Mui-selected': {
              fontWeight: 600,
              bgcolor: theme => `${theme.palette.primary.main}14`,
              '&:hover': { bgcolor: theme => `${theme.palette.primary.main}1F` },
            },
          }}
        >
          {label}
        </MenuItem>
      ))}
    </Select>
  );
};
